/**
 * Settings: separate music and sfx mutes, reduced motion, keyboard hints, and
 * the save code used to move progress between browsers (see docs/DEPLOY.md).
 */
import { isProfileCode } from '../systems/profileCode';
import { readQrFromPicture } from '../systems/saveTransfer';
import { confirmAndImportPlayer } from './ui/importPlayer';
import type { ImportOutcome } from './ui/importPlayer';
import Phaser from 'phaser';
import { audioOf } from '../systems/audioRegistry';
import { pressButton, styleButton } from './ui/menuButton';
import { setDevState } from '../systems/devQuery';
import { drawVerticalGradient, MENU_GRADIENT } from '../art/backdrop';
import { helperFor } from '../data/helpers';
import { gradeLabel } from './Grade';
import { GRADE_BANDS } from '../systems/WordPicker';
import type { ProfileSettings, SaveManager } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';
import { IconButton } from './ui/IconButton';

interface Row {
  readonly label: string;
  readonly help: string;
  readonly read: () => string;
  readonly activate: () => void;
}

/** Tall stage: help and status lines sit just under the nine rows. */
const SETTINGS_PORTRAIT_HELP_Y = 220 + 10 * 76 + 20;

export class Settings extends Phaser.Scene {
  private save!: SaveManager;
  private rows: Row[] = [];
  private labels: Phaser.GameObjects.Text[] = [];
  private values: Phaser.GameObjects.Text[] = [];
  private helpText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private selected = 0;

  constructor() {
    super('Settings');
  }

  create(): void {
    setDevState({ scene: 'Settings' });
    // Scene instances are reused, so clear anything that accumulates.
    this.rows = [];
    this.labels.length = 0;
    this.values.length = 0;
    this.selected = 0;
    this.save = this.registry.get('save') as SaveManager;
    this.cameras.main.setBackgroundColor(PALETTE.sky);
    this.drawBackdrop();

    this.add
      .text(GAME_WIDTH / 2, 70, 'Settings', {
        fontFamily: FONT_FAMILY,
        fontSize: '52px',
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 8,
      })
      .setOrigin(0.5);

    this.buildRows();
    this.drawRows();
    // A back button on every screen size: Esc alone left a mouse with no way out.
    new IconButton(
      this,
      56,
      IS_PORTRAIT ? 70 : 56,
      'back',
      () => this.scene.start('LevelMap'),
      IS_PORTRAIT ? 60 : 52,
    );

    this.helpText = this.add
      .text(GAME_WIDTH / 2, IS_PORTRAIT ? SETTINGS_PORTRAIT_HELP_Y : GAME_HEIGHT - 128, '', {
        fontFamily: FONT_FAMILY,
        fontSize: '20px',
        color: PALETTE.ice,
        align: 'center',
        wordWrap: { width: Math.min(900, GAME_WIDTH - 60) },
      })
      .setOrigin(0.5);

    this.statusText = this.add
      .text(GAME_WIDTH / 2, IS_PORTRAIT ? SETTINGS_PORTRAIT_HELP_Y + 80 : GAME_HEIGHT - 82, '', {
        fontFamily: FONT_FAMILY,
        fontSize: '20px',
        color: PALETTE.teal,
        align: 'center',
        wordWrap: { width: Math.min(980, GAME_WIDTH - 60) },
      })
      .setOrigin(0.5);

    if (!this.save.storageAvailable) {
      this.statusText.setText('This browser is blocking saves, so changes last only this visit.');
      this.statusText.setColor(PALETTE.sunset);
    }

    this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 40,
        IS_PORTRAIT ? 'Tap a row to change it' : 'Arrow keys choose, Enter changes, Esc goes back',
        {
          fontFamily: FONT_FAMILY,
          fontSize: '19px',
          color: PALETTE.ice,
        },
      )
      .setOrigin(0.5);

    this.installInput();
    this.select(0);
  }

  private buildRows(): void {
    const toggle = (key: keyof ProfileSettings): Row['activate'] => {
      return () => {
        const next = !this.save.settings()[key];
        this.save.setSetting(key, next);
        if (key === 'reducedMotion') this.registry.set('reducedMotion', next);
        this.applyMute();
        this.refresh();
      };
    };
    const onOff = (key: keyof ProfileSettings, invert = false): (() => string) => {
      return () => {
        const value = this.save.settings()[key];
        return (invert ? !value : value) ? 'On' : 'Off';
      };
    };

    this.rows = [
      {
        label: helperFor('musicMuted').label,
        help: helperFor('musicMuted').help,
        read: onOff('musicMuted', true),
        activate: toggle('musicMuted'),
      },
      {
        label: helperFor('sfxMuted').label,
        help: helperFor('sfxMuted').help,
        read: onOff('sfxMuted', true),
        activate: toggle('sfxMuted'),
      },
      {
        label: helperFor('reducedMotion').label,
        help: helperFor('reducedMotion').help,
        read: onOff('reducedMotion'),
        activate: toggle('reducedMotion'),
      },
      {
        label: helperFor('keyboardHints').label,
        help: helperFor('keyboardHints').help,
        read: onOff('keyboardHints'),
        activate: toggle('keyboardHints'),
      },
      {
        label: helperFor('autoEnter').label,
        help: helperFor('autoEnter').help,
        read: onOff('autoEnter'),
        activate: toggle('autoEnter'),
      },
      {
        label: helperFor('wordList').label,
        help: helperFor('wordList').help,
        read: onOff('wordList'),
        activate: toggle('wordList'),
      },
      {
        label: 'Grade',
        help: 'Picks words that are right for this grade. Every level stays open.',
        read: () => gradeLabel(this.save.grade()),
        activate: () => {
          // 3rd, 4th ... 8th, Not sure, and round again.
          const order = [...GRADE_BANDS, null];
          const next = order[(order.indexOf(this.save.grade()) + 1) % order.length] ?? null;
          this.save.setGrade(next);
          this.refresh();
        },
      },
      {
        label: 'Copy save code',
        help: 'Copies your progress as a code, to move it to another browser.',
        read: () => (IS_PORTRAIT ? 'Open' : 'Enter'),
        activate: () => void this.exportCode(),
      },
      {
        label: 'Load save code',
        help: 'Paste a save code or save link. A whole-browser code replaces ALL progress.',
        read: () => (IS_PORTRAIT ? 'Open' : 'Enter'),
        activate: () => void this.importCode(),
      },
      {
        label: 'Load save picture',
        help: 'Pick a Quest Jump save picture to bring that player onto this device.',
        read: () => (IS_PORTRAIT ? 'Open' : 'Enter'),
        activate: () => void this.importPicture(),
      },
    ];
  }

  private drawRows(): void {
    // Ten rows now: tightened so the last still clears the help text.
    // Tall stage: rows as wide as the screen allows and tall enough to tap.
    const top = IS_PORTRAIT ? 220 : 146;
    const rowHeight = IS_PORTRAIT ? 76 : 44;
    const half = Math.min(420, GAME_WIDTH / 2 - 24);

    this.rows.forEach((row, index) => {
      const y = top + index * rowHeight;
      const panel = this.add.graphics();
      // Lighter than every band of the backdrop, so each row stays visible.
      panel.fillStyle(0x24344f, 0.95);
      panel.fillRoundedRect(
        GAME_WIDTH / 2 - half,
        y - (IS_PORTRAIT ? 32 : 20),
        half * 2,
        IS_PORTRAIT ? 64 : 42,
        14,
      );

      const label = this.add
        .text(GAME_WIDTH / 2 - half + 30, y, row.label, {
          fontFamily: FONT_FAMILY,
          fontSize: '26px',
          color: PALETTE.snow,
        })
        .setOrigin(0, 0.5)
        .setInteractive({ useHandCursor: true });
      label.on('pointerover', () => this.select(index));
      label.on('pointerup', () => pressButton(label, row.activate));
      if (IS_PORTRAIT) {
        // The whole row is the button on a touch screen, not just its words.
        const hit = this.add
          .rectangle(GAME_WIDTH / 2, y, half * 2, 64, 0xffffff, 0)
          .setInteractive({ useHandCursor: true });
        hit.on('pointerup', () => {
          this.select(index);
          pressButton(label, row.activate);
        });
      }

      const value = this.add
        .text(GAME_WIDTH / 2 + half - 30, y, row.read(), {
          fontFamily: FONT_FAMILY,
          fontSize: '26px',
          color: PALETTE.gold,
        })
        .setOrigin(1, 0.5);

      this.labels.push(label);
      this.values.push(value);
    });
  }

  private installInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.scene.start('LevelMap');
        return;
      }
      if (event.key === 'ArrowUp') {
        this.select((this.selected + this.rows.length - 1) % this.rows.length);
      } else if (event.key === 'ArrowDown') {
        this.select((this.selected + 1) % this.rows.length);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const label = this.labels[this.selected];
        const row = this.rows[this.selected];
        if (label !== undefined && row !== undefined) pressButton(label, row.activate);
      }
    });
  }

  private select(index: number): void {
    this.selected = index;
    this.labels.forEach((label, i) => styleButton(label, i === index));
    this.helpText.setText(this.rows[index]?.help ?? '');
  }

  private refresh(): void {
    this.rows.forEach((row, index) => this.values[index]?.setText(row.read()));
  }

  private applyMute(): void {
    const settings = this.save.settings();
    this.sound.mute = settings.sfxMuted && settings.musicMuted;
    audioOf(this)?.applySettings(settings);
  }

  /* ------------------------------------------------------------ save codes */

  private async exportCode(): Promise<void> {
    const code = this.save.exportCode();
    const preview = `${code.slice(0, 10)}...${code.slice(-6)} (${code.length} characters)`;
    try {
      await navigator.clipboard.writeText(code);
      this.setStatus(`Copied to the clipboard: ${preview}`, PALETTE.teal);
    } catch {
      // Clipboard needs permission or a secure origin; show it instead.
      window.prompt('Copy this save code:', code);
      this.setStatus(`Save code shown: ${preview}`, PALETTE.gold);
    }
  }

  private async importPicture(): Promise<void> {
    const code = await readQrFromPicture().catch(() => null);
    if (code === null) {
      this.setStatus('No save picture was loaded. Pick the picture Quest Jump saved.', PALETTE.ice);
      return;
    }
    if (!isProfileCode(code)) {
      this.setStatus("That picture's code isn't a Quest Jump save.", PALETTE.sunset);
      return;
    }
    this.finishPlayerImport(confirmAndImportPlayer(this.save, code));
  }

  private finishPlayerImport(outcome: ImportOutcome): void {
    if (outcome === 'imported') {
      this.setStatus('Player loaded! Back to the players screen.', PALETTE.teal);
      this.time.delayedCall(1200, () => this.scene.start('ProfileSelect'));
    } else if (outcome === 'cancelled') {
      this.setStatus('Left your progress as it was.', PALETTE.ice);
    } else {
      this.setStatus('Nothing was changed.', PALETTE.ice);
    }
  }

  private async importCode(): Promise<void> {
    let code: string;
    try {
      code = await navigator.clipboard.readText();
    } catch {
      // Clipboard read needs permission; fall back to asking.
      code = '';
    }
    if (code.trim() === '') {
      code = window.prompt('Paste your save code:') ?? '';
    }
    if (code.trim() === '') {
      this.setStatus('Nothing pasted, so nothing changed.', PALETTE.ice);
      return;
    }

    // One player's code (from a QR code or save picture) only adds or updates
    // that player; it never replaces the whole browser.
    if (isProfileCode(code)) {
      this.finishPlayerImport(confirmAndImportPlayer(this.save, code));
      return;
    }

    const check = this.save.importCode.bind(this.save);
    const confirmed = window.confirm(
      'Loading a save code replaces ALL progress in this browser. Continue?',
    );
    if (!confirmed) {
      this.setStatus('Left your progress as it was.', PALETTE.ice);
      return;
    }

    const result = check(code);
    if (result.ok) {
      this.setStatus(
        'Progress loaded. Back to the players screen to pick a profile.',
        PALETTE.teal,
      );
      this.time.delayedCall(1400, () => this.scene.start('ProfileSelect'));
      return;
    }

    const reasons: Record<typeof result.reason, string> = {
      unreadable: 'That code could not be read. Check it was copied whole.',
      checksum: 'That code looks damaged, so it was not loaded.',
      version: 'That code came from a newer version of Quest Jump.',
      shape: 'That code is not a Quest Jump save.',
    };
    this.setStatus(reasons[result.reason], PALETTE.sunset);
  }

  private setStatus(message: string, color: string): void {
    this.statusText.setText(message);
    this.statusText.setColor(color);
  }

  private drawBackdrop(): void {
    drawVerticalGradient(this, MENU_GRADIENT);
  }
}
